import { createId } from "@paralleldrive/cuid2";
import { cn } from "@plasma/utils/cn";
import {
  ChevronDown,
  Copy,
  Layers,
  MoreHorizontal,
  Square,
  Trash2,
} from "lucide-react";
import type { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";

import type {
  ModifierFormSchema,
  ModifierGroupFormModifierListSchema,
} from "../../../lib/validators/modifiers";
import {
  AccordionContent,
  AccordionItem,
  AccordionTriggerButton,
} from "../../ui/accordion";
import { Button } from "../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import { Switch } from "../../ui/switch";
import { PriceField } from "./price-field";
import { SubmodifierModifiersListForm } from "./submodifier-list-form";

interface ModifierListItemProps {
  modifier: ModifierGroupFormModifierListSchema & { id: string };
  index: number;
  form: UseFormReturn<ModifierFormSchema>;
  fieldArray: UseFieldArrayReturn<ModifierFormSchema, "modifiersList">;
}

export function ModifierListItem({
  modifier,
  index,
  form,
  fieldArray,
}: ModifierListItemProps) {
  return (
    <AccordionItem
      className="border-border bg-background justify-between rounded-2xl border"
      key={modifier.itemId}
      value={modifier.itemId}
    >
      <div className="flex h-13 items-center justify-between px-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            {modifier.modifierType === "modifier" ? (
              <div
                className={cn(
                  "size-1.5 rounded-full",
                  modifier.data.isOutOfStock ? "bg-destructive" : "bg-success"
                )}
              />
            ) : null}
            <span className="label text-foreground">{modifier.name}</span>
            {modifier.modifierType === "modifier" ? (
              <span>{modifier.data.additionalCost}</span>
            ) : (
              <span>
                {modifier.data.modifiers?.length || 0} modifier
                {(modifier.data.modifiers?.length || 0) % 10 === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
        <div>
          <ModifierItemActions
            fieldArray={fieldArray}
            index={index}
            modifier={modifier}
          />
          <AccordionTriggerButton className="text-foreground hover:bg-accent inline-flex size-9 items-center justify-center rounded-md">
            <ChevronDown className="text-foreground size-5" />
          </AccordionTriggerButton>
        </div>
      </div>
      <AccordionContent
        className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden transition-all"
        defaultChecked
      >
        {modifier.modifierType === "modifier" ? (
          <ModifierFields form={form} index={index} />
        ) : (
          <SubmodifierFields form={form} index={index} />
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function ModifierItemActions({
  modifier,
  index,
  fieldArray,
}: {
  modifier: ModifierGroupFormModifierListSchema;
  index: number;
  fieldArray: UseFieldArrayReturn<ModifierFormSchema, "modifiersList">;
}) {
  return (
    <DropdownMenu>
      <Button asChild className="size-9 rounded-md" size="icon" variant="ghost">
        <DropdownMenuTrigger>
          <MoreHorizontal className="text-foreground size-5" />
        </DropdownMenuTrigger>
      </Button>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className="flex flex-row items-center gap-2"
          onClick={() => {
            if (modifier.modifierType === "modifier") {
              fieldArray.update(index, {
                itemId: modifier.itemId,
                modifierType: "sub_modifier",
                name: modifier.name,
                accordionOpen: true,
                sortOrder: index,
                data: {
                  minimum: 0,
                  maximum: 1,
                  modifiers: [],
                  parentModifierGroupId: null,
                  type: "sub_modifier",
                },
              });
            } else {
              fieldArray.update(index, {
                itemId: modifier.itemId,
                modifierType: "modifier",
                name: modifier.name,
                accordionOpen: true,
                sortOrder: index,
                data: {
                  additionalCost: "0.00",
                  isOutOfStock: false,
                  canBeMultiplied: false,
                },
              });
            }
          }}
        >
          {modifier.modifierType === "modifier" ? (
            <>
              <Layers className="size-4" />
              Convert to Submodifier group
            </>
          ) : (
            <>
              <Square className="size-4" />
              Convert to single modifier
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex flex-row items-center gap-2"
          onClick={() => {
            fieldArray.insert(index + 1, {
              ...modifier,
              itemId: createId(),
              sortOrder: index + 1,
            });
          }}
        >
          <Copy className="size-4" />
          Duplicate
        </DropdownMenuItem>
        <DropdownMenuItem
          className="flex flex-row items-center gap-2"
          onClick={() => fieldArray.remove(index)}
        >
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ModifierFields({
  form,
  index,
}: {
  form: UseFormReturn<ModifierFormSchema>;
  index: number;
}) {
  return (
    <div className="space-y-3 px-4 pt-2 pb-4">
      <div className="flex flex-row">
        <div className="flex-1">
          <FormField
            control={form.control}
            name={`modifiersList.${index}.data.isOutOfStock`}
            render={({ field }) => (
              <FormItem className="flex flex-row items-center">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="body-sm text-foreground">
                  Mark as out of stock
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex-1">
          <FormField
            control={form.control}
            name={`modifiersList.${index}.data.canBeMultiplied`}
            render={({ field }) => (
              <FormItem className="flex flex-row items-center">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="body-sm text-foreground">
                  This Modifier can be multiplied
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name={`modifiersList.${index}.name`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input
                  className="body-sm h-9 rounded-md px-3 py-2"
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  placeholder="New modifier"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <PriceField control={form.control} index={index} />
      </div>
    </div>
  );
}

function SubmodifierFields({
  form,
  index,
}: {
  form: UseFormReturn<ModifierFormSchema>;
  index: number;
}) {
  return (
    <div className="space-y-3 px-4 pt-2">
      <FormField
        control={form.control}
        name={`modifiersList.${index}.name`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Submodifier Group Name</FormLabel>
            <FormControl>
              <Input
                {...field}
                className="body-sm placeholder:body-sm h-9 rounded-md px-3 py-2 leading-tight"
                placeholder="New submodifier group"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-2 gap-5">
        <FormField
          control={form.control}
          name={`modifiersList.${index}.data.minimum`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Minimum Selection</FormLabel>
              <FormControl>
                <Input
                  className="body-sm h-9 rounded-md px-3 py-2 leading-tight"
                  min={0}
                  onChange={(e) => {
                    const value = e.currentTarget.valueAsNumber;
                    field.onChange(Number.isNaN(value) ? 0 : value);
                  }}
                  onFocus={(e) => e.target.select()}
                  type="number"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name={`modifiersList.${index}.data.maximum`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Maximum Selection</FormLabel>
              <FormControl>
                <Input
                  className="body-sm h-9 rounded-md px-3 py-2 leading-tight"
                  min={1}
                  onChange={(e) => {
                    const value = e.currentTarget.valueAsNumber;
                    field.onChange(Number.isNaN(value) ? 1 : value);
                  }}
                  onFocus={(e) => e.target.select()}
                  type="number"
                  value={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="border-border h-px w-full border-t" />
      <SubmodifierModifiersListForm index={index} />
    </div>
  );
}
